import { conditionLabel, type ListingPriceLine } from '@auctisync/api-client';
import { Badge } from '@auctisync/ui';
import { formatCents, detailUrl, thumbUrl } from '@auctisync/shared';
import type { ReactNode } from 'react';

/**
 * Bidder-view preview modal.
 *
 * Renders a scaled-down approximation of the public lot page (see
 * PublicLotDetail.tsx) using values from the admin's in-progress form.
 * Shows unsaved AI Complete edits, new photos, reworded descriptions —
 * everything as a bidder would see it after Save + lot publish.
 *
 * Not 100% fidelity (no live countdown, no real bid form, no anti-snipe
 * messaging) — this is a content preview, not a functional simulator.
 */
export function BidderPreviewModal({
  photos,
  title,
  condition,
  startingBidCents,
  reserveCents,
  description,
  specs,
  prices,
  onClose,
}: {
  photos: Array<{ display_url: string; caption?: string | null }>;
  title: string;
  condition: string;
  startingBidCents: number | null;
  reserveCents: number | null;
  description: string;
  specs: Record<string, unknown>;
  /** The prices the listing shows, from the shared `listingPriceLines` (Jim, 2026-10-06). */
  prices: readonly ListingPriceLine[];
  onClose: () => void;
}) {
  const primary = photos[0];
  const rest = photos.slice(1, 8);

  return (
    <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="Bidder preview">
      <div style={modalStyle}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
              Preview · bidder view
            </h2>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Renders with current form values (including unsaved AI edits). The real lot page also
              shows countdown, live bidding, and anti-snipe messaging.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.4rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </header>

        <div
          style={{
            display: 'grid',
            gap: '1.5rem',
            gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
          }}
        >
          {/* Left: photos */}
          <div>
            <div
              style={{
                aspectRatio: '4 / 3',
                background: 'var(--surface-muted)',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                marginBottom: '0.75rem',
                position: 'relative',
              }}
            >
              {primary ? (
                <img
                  src={detailUrl(primary.display_url)}
                  alt={title || 'Lot photo'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    fontSize: '0.9rem',
                  }}
                >
                  No photos uploaded yet
                </div>
              )}
            </div>
            {rest.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gap: '0.4rem',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(4rem, 1fr))',
                }}
              >
                {rest.map((p, i) => (
                  <img
                    key={i}
                    src={thumbUrl(p.display_url)}
                    alt={p.caption ?? ''}
                    loading="lazy"
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      borderRadius: '0.3rem',
                      border: '1px solid var(--border)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: content */}
          <div>
            <div
              style={{
                display: 'flex',
                gap: '0.4rem',
                alignItems: 'center',
                marginBottom: '0.5rem',
                flexWrap: 'wrap',
              }}
            >
              <Badge variant="success">Active</Badge>
              {reserveCents !== null && <Badge variant="outline">Reserve set</Badge>}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'var(--text)',
                margin: '0 0 0.4rem',
              }}
            >
              {title || <span style={{ color: 'var(--text-muted)' }}>(no title yet)</span>}
            </h1>
            {condition && (
              <div
                style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}
              >
                Condition:{' '}
                <strong style={{ color: 'var(--text)' }}>
                  {conditionLabel(condition) ?? condition}
                </strong>
              </div>
            )}

            <div
              style={{
                padding: '0.85rem 1rem',
                borderRadius: '0.5rem',
                background: 'var(--surface-elev)',
                border: '1px solid var(--border)',
                marginBottom: '0.75rem',
                display: 'grid',
                gap: '0.35rem',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Starting bid
                </span>
                <span
                  style={{
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    color: 'var(--text)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {startingBidCents !== null ? (
                    formatCents(startingBidCents)
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>not set</span>
                  )}
                </span>
              </div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                }}
              >
                Live countdown + bid form shown on the real lot page.
              </div>
            </div>

            {prices.length > 0 && (
              <section style={{ marginBottom: '0.9rem' }} data-testid="preview-prices">
                {prices.map((p) => (
                  <div
                    key={p.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.9rem',
                      padding: '0.2rem 0',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>{p.label}</span>
                    <strong style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {p.value}
                    </strong>
                  </div>
                ))}
              </section>
            )}

            {hasAnySpec(specs) && (
              <section style={{ marginBottom: '0.9rem' }}>
                <SectionHeading>Specifications</SectionHeading>
                <SpecsGrid specs={specs} />
              </section>
            )}

            {description && (
              <section>
                <SectionHeading>Description</SectionHeading>
                <p
                  style={{
                    color: 'var(--text)',
                    fontSize: '0.9rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {description}
                </p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2
      style={{
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        margin: '0 0 0.3rem',
      }}
    >
      {children}
    </h2>
  );
}

const SPEC_ORDER = ['brand', 'model', 'year', 'material', 'color', 'dimensions'];
const SPEC_LABELS: Record<string, string> = {
  brand: 'Brand',
  model: 'Model',
  year: 'Year',
  material: 'Material',
  color: 'Color',
  dimensions: 'Dimensions',
};

function hasAnySpec(specs: Record<string, unknown>): boolean {
  return Object.values(specs).some(
    (v) =>
      v !== null &&
      v !== undefined &&
      v !== '' &&
      (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'),
  );
}

function SpecsGrid({ specs }: { specs: Record<string, unknown> }) {
  const rows: Array<{ label: string; value: string }> = [];
  for (const key of SPEC_ORDER) {
    const display = formatSpecValue(specs[key]);
    if (display) rows.push({ label: SPEC_LABELS[key] ?? key, value: display });
  }
  for (const [k, v] of Object.entries(specs)) {
    if (SPEC_LABELS[k]) continue;
    const display = formatSpecValue(v);
    if (display) rows.push({ label: k.replace(/_/g, ' '), value: display });
  }
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: '1rem',
        rowGap: '0.25rem',
        margin: 0,
        padding: '0.6rem 0.85rem',
        background: 'var(--surface-elev)',
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        fontSize: '0.85rem',
      }}
    >
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'contents' }}>
          <dt style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{r.label}</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatSpecValue(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return '';
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '2rem 1rem',
  zIndex: 120,
  overflowY: 'auto',
};

const modalStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  maxWidth: '64rem',
  width: '100%',
  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
};
